import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Activity, Globe2, MonitorSmartphone, MousePointerClick, RefreshCw, ShoppingCart, UserPlus } from "lucide-react";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const PIE_COLORS = ["#0f766e", "#f59e0b", "#2563eb", "#8b5cf6", "#ef4444", "#475569"];
const HEAT_COLORS = ["#c7f9cc", "#80ed99", "#57cc99", "#38a3a5", "#22577a"];

const COUNTRY_POINTS = {
  BR: { x: 39, y: 65, label: "Brasil" },
  US: { x: 20, y: 35, label: "Estados Unidos" },
  CA: { x: 20, y: 21, label: "Canadá" },
  MX: { x: 16, y: 45, label: "México" },
  AR: { x: 33, y: 82, label: "Argentina" },
  CL: { x: 29, y: 77, label: "Chile" },
  CO: { x: 31, y: 54, label: "Colômbia" },
  PE: { x: 30, y: 61, label: "Peru" },
  PT: { x: 49, y: 37, label: "Portugal" },
  ES: { x: 51, y: 39, label: "Espanha" },
  FR: { x: 53, y: 35, label: "França" },
  DE: { x: 55, y: 31, label: "Alemanha" },
  IT: { x: 56, y: 40, label: "Itália" },
  GB: { x: 51, y: 29, label: "Reino Unido" },
  IE: { x: 49, y: 28, label: "Irlanda" },
  NL: { x: 54, y: 30, label: "Holanda" },
  NG: { x: 57, y: 54, label: "Nigéria" },
  ZA: { x: 58, y: 80, label: "África do Sul" },
  IN: { x: 69, y: 49, label: "Índia" },
  CN: { x: 78, y: 40, label: "China" },
  JP: { x: 86, y: 42, label: "Japão" },
  AU: { x: 84, y: 78, label: "Austrália" },
};

const shortDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const WorldHeatmap = ({ countries }) => {
  const maxCount = Math.max(...countries.map((item) => item.count), 1);
  const visibleCountries = countries.filter((item) => COUNTRY_POINTS[item.country_code]);

  const colorForCount = (count) => {
    if (count <= 0) return HEAT_COLORS[0];
    const ratio = count / maxCount;
    if (ratio > 0.8) return HEAT_COLORS[4];
    if (ratio > 0.6) return HEAT_COLORS[3];
    if (ratio > 0.4) return HEAT_COLORS[2];
    if (ratio > 0.2) return HEAT_COLORS[1];
    return HEAT_COLORS[0];
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="relative mx-auto w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-sky-50 shadow-inner">
        <div className="relative w-full" style={{ aspectRatio: "950 / 620" }}>
          <img
            src="/assets/world-map.svg"
            alt="Mapa mundi"
            className="absolute inset-0 h-full w-full object-contain opacity-100"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-sky-100/10 via-transparent to-sky-100/15" />
          {visibleCountries.map((item) => {
            const point = COUNTRY_POINTS[item.country_code];
            const radius = 10 + (item.count / maxCount) * 18;
            return (
              <div
                key={item.country_code}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${point.x}%`, top: `${point.y}%` }}
              >
                <div
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    width: `${radius * 2 + 18}px`,
                    height: `${radius * 2 + 18}px`,
                    backgroundColor: colorForCount(item.count),
                    opacity: 0.18,
                    left: "50%",
                    top: "50%",
                  }}
                />
                <div
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/80 shadow-md"
                  style={{
                    width: `${radius * 2}px`,
                    height: `${radius * 2}px`,
                    backgroundColor: colorForCount(item.count),
                    left: "50%",
                    top: "50%",
                  }}
                />
                <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow" />
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2" style={{ marginTop: `${radius + 12}px` }}>
                  <div className="rounded-md bg-white/90 px-2 py-1 text-center shadow-sm ring-1 ring-slate-200">
                    <p className="whitespace-nowrap text-[11px] font-semibold leading-none text-slate-700">{point.label}</p>
                    <p className="mt-1 text-xs font-bold leading-none text-slate-900">{item.count}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ProgrammerAnalytics = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [data, setData] = useState(null);

  const fetchOverview = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/programmer/analytics/overview`, {
        withCredentials: true,
        params: {
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
        },
      });
      setData(response.data);
      setLastUpdatedAt(new Date());
    } catch (error) {
      if (!silent) {
        toast.error(error.response?.data?.detail || "Erro ao carregar analytics");
      }
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchOverview({ silent: true });
      }
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [fetchOverview]);

  const summaryCards = useMemo(() => {
    if (!data?.summary) return [];
    return [
      {
        label: "Visitantes da landing",
        value: data.summary.landing_visitors,
        note: `${data.summary.landing_views} visualizações`,
        icon: Globe2,
      },
      {
        label: "Tentativas de cadastro",
        value: data.summary.register_attempts,
        note: `${data.summary.register_successes} concluídos`,
        icon: UserPlus,
      },
      {
        label: "Diagnósticos iniciados",
        value: data.summary.diagnostics_started,
        note: `${data.summary.payment_page_views} chegaram ao pagamento`,
        icon: Activity,
      },
      {
        label: "Compras concluídas",
        value: data.summary.diagnostics_paid + data.summary.diagnostics_released_by_coupon,
        note: `${data.summary.checkout_generated} checkouts gerados`,
        icon: ShoppingCart,
      },
    ];
  }, [data]);

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-serif text-3xl text-navy">Analytics de Jornada</h1>
            <p className="text-slate-600">Visitas, cadastros, diagnósticos e compras em um painel só para o programador.</p>
            <p className="mt-2 text-xs text-slate-500">
              {lastUpdatedAt ? `Última atualização: ${shortDate(lastUpdatedAt)}` : "Aguardando primeira leitura"}
              {refreshing ? " · atualizando..." : ""}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 w-full md:w-auto">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <Button onClick={fetchOverview} className="bg-navy hover:bg-navy/90">Aplicar período</Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => fetchOverview({ silent: true })}
              className="gap-2"
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
          </div>
        ) : !data ? null : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {summaryCards.map((card) => (
                <Card key={card.label} className="border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-slate-500">{card.label}</span>
                      <card.icon className="h-4 w-4 text-emerald-600" />
                    </div>
                    <p className="text-3xl font-bold text-navy">{card.value}</p>
                    <p className="text-xs text-slate-500 mt-2">{card.note}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Funil principal</CardTitle>
                  <CardDescription>Da visita inicial até a compra do diagnóstico.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={data.funnel} layout="vertical" margin={{ left: 32 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="step" width={150} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#0f766e" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Evolução por dia</CardTitle>
                  <CardDescription>Visitas, cadastros, checkouts e compras por período.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={data.daily_series}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="visitas" stroke="#0f766e" strokeWidth={2} />
                      <Line type="monotone" dataKey="cadastros_concluidos" stroke="#2563eb" strokeWidth={2} />
                      <Line type="monotone" dataKey="checkouts_gerados" stroke="#f59e0b" strokeWidth={2} />
                      <Line type="monotone" dataKey="compras_concluidas" stroke="#8b5cf6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1.4fr,1fr] gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <Globe2 className="h-5 w-5 text-emerald-600" />
                    Mapa de calor por país
                  </CardTitle>
                  <CardDescription>Leitura inicial da origem geográfica aproximada dos acessos.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <WorldHeatmap countries={data.countries || []} />
                  <div className="grid gap-2 md:grid-cols-2">
                    {(data.countries || []).slice(0, 8).map((item) => (
                      <div key={item.country_code} className="rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between">
                        <span className="text-sm text-slate-600">{item.country_code}</span>
                        <Badge className="bg-slate-100 text-slate-800">{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <MonitorSmartphone className="h-5 w-5 text-sky-600" />
                      Dispositivos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={data.devices || []} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label>
                          {(data.devices || []).map((entry, index) => (
                            <Cell key={entry.label} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl">Navegadores mais usados</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(data.browsers || []).slice(0, 6).map((item) => (
                      <div key={item.label} className="flex items-center justify-between gap-4">
                        <span className="text-sm text-slate-600">{item.label}</span>
                        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-navy"
                            style={{ width: `${Math.min(100, ((item.count || 0) / Math.max(...(data.browsers || []).map((entry) => entry.count), 1)) * 100)}%` }}
                          />
                        </div>
                        <Badge className="bg-slate-100 text-slate-800">{item.count}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1fr,1.2fr] gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Páginas mais acessadas</CardTitle>
                  <CardDescription>Rotas com mais interação no período filtrado.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(data.top_pages || []).map((item) => (
                    <div key={item.label} className="flex items-center gap-4">
                      <MousePointerClick className="h-4 w-4 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-navy truncate">{item.label}</p>
                      </div>
                      <Badge className="bg-slate-100 text-slate-800">{item.count}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Últimos eventos</CardTitle>
                  <CardDescription>Visão rápida do que está acontecendo na jornada.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 max-h-[420px] overflow-auto">
                  {(data.recent_events || []).map((event) => (
                    <div key={event.event_id} className="rounded-xl border border-slate-200 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800">{event.event_name}</Badge>
                        {event.country_code && <Badge className="bg-slate-100 text-slate-700">{event.country_code}</Badge>}
                        {event.device_type && <Badge className="bg-sky-100 text-sky-800">{event.device_type}</Badge>}
                      </div>
                      <p className="mt-2 text-sm font-medium text-navy break-all">{event.path || "Sem rota"}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {shortDate(event.created_at)} · {event.browser || "Navegador"} · {event.os || "SO"}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProgrammerAnalytics;
